import React, { useState, useEffect } from 'react';
import { reportService } from '../../services/api';
import { Package, ShoppingCart, Users, IndianRupee, Clock, AlertTriangle, Store, Truck } from 'lucide-react';

function DashboardOverview() {
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    total_users: 0,
    total_revenue: 0,
    pending_orders: 0,
    low_stock_items: 0,
    active_dealers: 0,
    active_distributors: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const data = await reportService.dashboard();
      setStats(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading dashboard...</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-slate-100">Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Products</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">{stats.total_products}</p>
            </div>
            <div className="bg-red-600/10 p-3 rounded-lg text-red-50">
              <Package size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Orders</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">{stats.total_orders}</p>
            </div>
            <div className="bg-blue-600/10 p-3 rounded-lg text-blue-500">
              <ShoppingCart size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Users</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">{stats.total_users}</p>
            </div>
            <div className="bg-purple-600/10 p-3 rounded-lg text-purple-500">
              <Users size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Revenue</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">₹{stats.total_revenue}</p>
            </div>
            <div className="bg-amber-600/10 p-3 rounded-lg text-amber-500">
              <IndianRupee size={24} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Pending Orders</p>
              <p className="text-2xl font-bold text-slate-100 mt-1">{stats.pending_orders || 0}</p>
            </div>
            <div className="text-2xl text-yellow-500 opacity-80">
              <Clock size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Low Stock Items</p>
              <p className="text-2xl font-bold text-slate-100 mt-1">{stats.low_stock_items}</p>
            </div>
            <div className="text-2xl text-red-500 opacity-80">
              <AlertTriangle size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Active Dealers</p>
              <p className="text-2xl font-bold text-slate-100 mt-1">{stats.active_dealers || 0}</p>
            </div>
            <div className="text-2xl text-red-500 opacity-80">
              <Store size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Active Distributors</p>
              <p className="text-2xl font-bold text-slate-100 mt-1">{stats.active_distributors || 0}</p>
            </div>
            <div className="text-2xl text-blue-500 opacity-80">
              <Truck size={24} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-slate-100">Quick Actions</h3>
          <div className="space-y-3">
            <a href="/admin?tab=products" className="flex items-center p-3 bg-slate-950 border border-slate-800 rounded-lg hover:border-red-600/40 hover:bg-slate-800/60 transition group">
              <Package className="mr-3 text-red-500 opacity-80 group-hover:opacity-100" size={20} />
              <span className="font-medium text-slate-300 group-hover:text-slate-100">Add New Product</span>
            </a>
            <a href="/admin?tab=orders" className="flex items-center p-3 bg-slate-950 border border-slate-800 rounded-lg hover:border-blue-600/40 hover:bg-slate-800/60 transition group">
              <ShoppingCart className="mr-3 text-blue-500 opacity-80 group-hover:opacity-100" size={20} />
              <span className="font-medium text-slate-300 group-hover:text-slate-100">View Orders</span>
            </a>
            <a href="/admin?tab=users" className="flex items-center p-3 bg-slate-950 border border-slate-800 rounded-lg hover:border-purple-600/40 hover:bg-slate-800/60 transition group">
              <Users className="mr-3 text-purple-500 opacity-80 group-hover:opacity-100" size={20} />
              <span className="font-medium text-slate-300 group-hover:text-slate-100">Manage Users</span>
            </a>
            <a href="/admin?tab=inventory" className="flex items-center p-3 bg-slate-950 border border-slate-800 rounded-lg hover:border-amber-600/40 hover:bg-slate-800/60 transition group">
              <Package className="mr-3 text-amber-500 opacity-80 group-hover:opacity-100" size={20} />
              <span className="font-medium text-slate-300 group-hover:text-slate-100">Check Inventory</span>
            </a>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-slate-100">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-start p-3 border-b border-slate-800/50">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-slate-200">System initialized</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Just now</p>
              </div>
            </div>
            <div className="flex items-start p-3 border-b border-slate-800/50">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-slate-200">Database migrations completed</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Recently</p>
              </div>
            </div>
            <div className="flex items-start p-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-slate-200">Backend and frontend servers running</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Recently</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardOverview;
